import * as React from 'react';
import { Form, Input, message, Select } from 'antd';
import { ModalProps } from 'antd/es/modal';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { apiCreateGroup } from 'api/groups';
import t from 'utils/translations';
import { AddCategoryResponse } from 'components/context/types';
import ModalBase from 'components/common/ModalBase';
import { validateNameRegex } from 'utils/validation';
import { apiCreateCategory } from 'api/categories';

const CategoryOption = styled(Select.Option)`
    &:first-of-type {
    }
`;

const NewCategoryFormItem = styled(Form.Item)`
    && {
        margin-bottom: 0;
    }
`;

export interface CategoryInfo {
    categoryName: string;
    categoryId: string;
}

interface Props extends ModalProps {
    afterSuccess: () => void;
    categories: CategoryInfo[];
    closeModal: () => void;
    preSelectedCategoryId?: string;
}

const NEW_CATEGORY_ID = 'newCategory';

function CreateGroupModal({
    afterSuccess,
    categories,
    closeModal,
    preSelectedCategoryId,
    visible
}: Props) {
    const [selectedCategory, setSelectedCategory] = useState<
        string | undefined
    >(undefined);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newGroupName, setNewGroupName] = useState('');

    const [categoriesToShow, setCategoriesToShow] = useState<CategoryInfo[]>(
        []
    );

    const [emptyCategoryNameError, setEmptyCategoryNameError] = useState(false);
    const [invalidCategoryNameError, setInvalidCategoryNameError] = useState(
        false
    );

    const [emptyGroupNameError, setEmptyGroupNameError] = useState(false);
    const [invalidGroupNameError, setInvalidGroupNameError] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setCategoriesToShow([
            {
                categoryName: `- ${t('CREATE_NEW_CATEGORY_OPTION')} -`,
                categoryId: NEW_CATEGORY_ID
            },
            ...categories
        ]);
        if (preSelectedCategoryId) {
            handleCategorySelectChange(preSelectedCategoryId);
        }
    }, [categories]);

    function createCategory() {
        return apiCreateCategory(newCategoryName)
            .then(res => {
                if (res.status === 200) {
                    message.success(t('CATEGORY_CREATE_SUCCESS'));
                }
                return res.json();
            })
            .catch(() => message.error(t('CATEGORY_CREATE_FAIL')));
    }

    function createGroup(selectedCategoryParam?: string) {
        return apiCreateGroup(newGroupName, selectedCategoryParam)
            .then(res => {
                if (res.status === 200) {
                    message.success(t('GROUP_CREATE_SUCCESS'));
                    resetModal();
                    afterSuccess();
                    closeModal();
                }
            })
            .catch(() => {
                message.error(t('GROUP_CREATE_FAIL'));
            });
    }

    function handleCancel() {
        resetModal();
        closeModal();
    }

    function handleOk() {
        if (validateForm()) {
            setIsLoading(true);
            if (selectedCategory === NEW_CATEGORY_ID) {
                createCategory().then((category: AddCategoryResponse) =>
                    createGroup(category.categoryId).finally(() =>
                        setIsLoading(false)
                    )
                );
            } else {
                createGroup(selectedCategory).finally(() =>
                    setIsLoading(false)
                );
            }
        }
    }

    function handleCategorySelectChange(categoryId: string) {
        setSelectedCategory(categoryId);
    }

    function handleCategoryNameChange(e: any) {
        const name = e.target.value;

        if (validateNameRegex(name)) {
            setInvalidCategoryNameError(false);
        } else {
            setInvalidCategoryNameError(true);
        }
        setEmptyCategoryNameError(false);
        setNewCategoryName(name);
    }

    function handleGroupNameChange(e: any) {
        const name = e.target.value;

        if (validateNameRegex(name)) {
            setInvalidGroupNameError(false);
        } else {
            setInvalidGroupNameError(true);
        }
        setEmptyGroupNameError(false);
        setNewGroupName(name);
    }

    function resetModal() {
        setSelectedCategory(undefined);
        setNewGroupName('');
        setNewCategoryName('');

        setEmptyCategoryNameError(false);
        setInvalidCategoryNameError(false);

        setEmptyGroupNameError(false);
        setInvalidGroupNameError(false);
    }

    function selectCategoryNameHelpMessage() {
        if (emptyCategoryNameError) {
            return t('EMPTY_NAME_ERROR');
        } else if (invalidCategoryNameError) {
            return t('INVALID_NAME_ERROR');
        }
        return undefined;
    }

    function selectGroupNameHelpMessage() {
        if (emptyGroupNameError) {
            return t('EMPTY_NAME_ERROR');
        } else if (invalidGroupNameError) {
            return t('INVALID_NAME_ERROR');
        }
        return undefined;
    }

    function validateForm() {
        let isValid = true;

        if (newGroupName.length === 0) {
            setEmptyGroupNameError(true);
            isValid = false;
        } else {
            setEmptyGroupNameError(false);
        }

        if (!validateNameRegex(newGroupName)) {
            setInvalidGroupNameError(true);
            isValid = false;
        } else {
            setInvalidGroupNameError(false);
        }

        if (
            selectedCategory === NEW_CATEGORY_ID &&
            newCategoryName.length === 0
        ) {
            setEmptyCategoryNameError(true);
            isValid = false;
        } else {
            setEmptyCategoryNameError(false);
        }

        if (
            selectedCategory === NEW_CATEGORY_ID &&
            !validateNameRegex(newCategoryName)
        ) {
            setInvalidCategoryNameError(true);
            isValid = false;
        } else {
            setInvalidCategoryNameError(false);
        }

        return isValid;
    }

    return (
        <ModalBase
            cancelText={t('CANCEL_MODAL')}
            confirmLoading={isLoading}
            destroyOnClose={true}
            onCancel={() => handleCancel()}
            onOk={() => handleOk()}
            okText={t('CREATE_GROUP')}
            title={t('CREATE_GROUP_MODAL_TITLE')}
            visible={visible}
        >
            <Form>
                <Form.Item colon={false} label={t('SELECT_CATEGORY')}>
                    <Select
                        allowClear={true}
                        onChange={(categoryId: string) =>
                            handleCategorySelectChange(categoryId)
                        }
                        value={selectedCategory}
                    >
                        {categoriesToShow.map(category => (
                            <CategoryOption
                                key={category.categoryId}
                                value={category.categoryId}
                            >
                                {category.categoryName}
                            </CategoryOption>
                        ))}
                    </Select>
                    {selectedCategory === NEW_CATEGORY_ID && (
                        <NewCategoryFormItem
                            colon={false}
                            help={selectCategoryNameHelpMessage()}
                            validateStatus={
                                emptyCategoryNameError ||
                                invalidCategoryNameError
                                    ? 'error'
                                    : undefined
                            }
                            label={t('CATEGORY_NAME_LABEL')}
                        >
                            <Input
                                onChange={e => handleCategoryNameChange(e)}
                                maxLength={50}
                                value={newCategoryName}
                            />
                        </NewCategoryFormItem>
                    )}
                </Form.Item>

                <Form.Item
                    colon={false}
                    help={selectGroupNameHelpMessage()}
                    validateStatus={
                        emptyGroupNameError || invalidGroupNameError
                            ? 'error'
                            : undefined
                    }
                    label={t('GROUP_NAME')}
                >
                    <Input
                        onChange={e => handleGroupNameChange(e)}
                        maxLength={50}
                        value={newGroupName}
                    />
                </Form.Item>
            </Form>
        </ModalBase>
    );
}

export default CreateGroupModal;
